/**
 * useCalibrationTakes — hook for the calibration takes panel (T6, B-2026-0126).
 *
 * Mirrors the pattern from useTeachingVideo.ts:
 * - useQuery for the take list (refetches after upload/delete).
 * - useQuery for individual take status, polling until terminal state.
 * - useMutation for upload, process, delete.
 *
 * Polling pattern: a take is polled while its status is 'uploaded' or
 * 'processing'. Polling stops automatically at 'completed' or 'failed'.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listCalibrationTakes,
  uploadCalibrationTake,
  processCalibrationTake,
  getCalibrationTakeStatus,
  deleteCalibrationTake,
  getCalibrationStatus,
  updateCalibrationTake,
  recalibrateTakes,
  listVisibleExamplePerformances,
} from '@/services/templateApi';
import type {
  CalibrationGradeLabel,
  CalibrationTakeStatus,
  CalibrationTakeListResponse,
  CalibrationTakeUpdateRequest,
} from '@/types/calibrationTake';

const TERMINAL_STATUSES = new Set<CalibrationTakeStatus>(['completed', 'failed']);
const POLLING_INTERVAL_MS = 2_000;

// ---------------------------------------------------------------------------
// Take list query
// ---------------------------------------------------------------------------

export function useCalibrationTakeList(templateId: string | undefined) {
  return useQuery({
    queryKey: ['calibration-takes', templateId],
    queryFn: () => listCalibrationTakes(templateId!),
    enabled: !!templateId,
    staleTime: 5_000,
  });
}

// ---------------------------------------------------------------------------
// Per-take status polling query
// ---------------------------------------------------------------------------

/**
 * Poll a single calibration take until it reaches a terminal state.
 * Pass undefined to disable.
 */
export function useCalibrationTakeStatus(
  templateId: string | undefined,
  takeId: string | undefined,
) {
  return useQuery({
    queryKey: ['calibration-take-status', templateId, takeId],
    queryFn: () => getCalibrationTakeStatus(templateId!, takeId!),
    enabled: !!(templateId && takeId),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (!status || TERMINAL_STATUSES.has(status)) return false;
      return POLLING_INTERVAL_MS;
    },
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Calibration summary query
// ---------------------------------------------------------------------------

export function useCalibrationStatus(templateId: string | undefined) {
  return useQuery({
    queryKey: ['calibration-status', templateId],
    queryFn: () => getCalibrationStatus(templateId!),
    enabled: !!templateId,
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// Upload mutation
// ---------------------------------------------------------------------------

export function useUploadCalibrationTake(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      label,
      failureDimension,
      reasonText,
      gradeLabel,
      gradeNumeric,
    }: {
      file: File;
      label: 'good' | 'bad' | 'fail';
      failureDimension?: 'motion' | 'activity' | 'angle' | null;
      reasonText?: string | null;
      /** Legacy owner-band grade — no longer collected by the uploader UI. */
      gradeLabel?: CalibrationGradeLabel | null;
      /** 0-100 numeric grade (numeric take-grade entry) — what the uploader UI sends. */
      gradeNumeric?: number | null;
    }) =>
      uploadCalibrationTake(
        templateId,
        file,
        label,
        failureDimension,
        reasonText,
        gradeLabel,
        gradeNumeric,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calibration-takes', templateId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Process mutation
// ---------------------------------------------------------------------------

export function useProcessCalibrationTake(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (takeId: string) => processCalibrationTake(templateId, takeId),
    onSuccess: (_data, takeId) => {
      // Seed the status cache so polling starts immediately
      queryClient.invalidateQueries({
        queryKey: ['calibration-take-status', templateId, takeId],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Delete mutation
// ---------------------------------------------------------------------------

export function useDeleteCalibrationTake(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (takeId: string) => deleteCalibrationTake(templateId, takeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calibration-takes', templateId] });
      queryClient.invalidateQueries({ queryKey: ['calibration-status', templateId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Update mutation (take-edit-frontend: declared_lead_side / failure_dimensions)
// ---------------------------------------------------------------------------

/**
 * Edit a take's declared lead side and/or failure-dimension tags.
 *
 * Like delete, this re-triggers calibration server-side, so both the take
 * list and the calibration summary are invalidated on success (same two
 * query keys `useDeleteCalibrationTake` invalidates).
 *
 * Two extra steps beyond the delete pattern, both aimed at the same
 * requirement — the row must show the new 'processing' state, not a frozen
 * stale one, right after a save:
 *  1. `setQueryData` merges the PATCH response into the cached take list
 *     immediately, so the row updates before the invalidated refetch lands.
 *  2. `removeQueries` drops any cached per-take status-polling result for
 *     this take. That query is disabled once a take reaches a terminal
 *     status ('completed'/'failed') but keeps its last-fetched data in the
 *     cache; if an edit flips the take back to 'processing' the polling
 *     hook (`useCalibrationTakeStatus`) re-enables and would otherwise show
 *     that stale terminal snapshot for one paint before its own refetch
 *     resolves (`CalibrationTakeList`'s `displayTake = polledTake ?? take`
 *     prefers the polled value). Removing it makes the row fall through to
 *     the fresh `take` from step 1 with no stale flash.
 */
export function useUpdateCalibrationTake(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      takeId,
      updates,
    }: {
      takeId: string;
      updates: CalibrationTakeUpdateRequest;
    }) => updateCalibrationTake(templateId, takeId, updates),
    onSuccess: (updatedTake, { takeId }) => {
      queryClient.setQueryData<CalibrationTakeListResponse | undefined>(
        ['calibration-takes', templateId],
        (old) =>
          old
            ? {
                ...old,
                takes: old.takes.map((t) =>
                  t.take_id === takeId ? { ...t, ...updatedTake } : t,
                ),
              }
            : old,
      );
      queryClient.removeQueries({
        queryKey: ['calibration-take-status', templateId, takeId],
      });
      queryClient.invalidateQueries({ queryKey: ['calibration-takes', templateId] });
      queryClient.invalidateQueries({ queryKey: ['calibration-status', templateId] });
      // Example Performances (PR 1+2): a visibility/consent/note edit here
      // changes what the athlete-facing panel shows — invalidate its
      // separate cache key too, not just the coach-side takes list.
      queryClient.invalidateQueries({ queryKey: ['example-performances', templateId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Recalibrate ("Re-measure takes" action, backlog: recompile-button-remeasure-gap.md)
// ---------------------------------------------------------------------------

/**
 * Trigger a full re-measurement of every calibration take under the current
 * scoring version, and track completion.
 *
 * POST .../recalibrate is fire-and-forget (202, no job id on the wire — the
 * backend coalesces repeat calls into the same in-flight run). Completion is
 * detected the same way the endpoint's own docstring describes: poll the
 * calibration summary and watch `computed_at` for a change from the value
 * captured immediately before the POST.
 *
 * `trigger(baselineComputedAt)` takes the CURRENT `computed_at` (from the
 * summary already loaded by the caller) so the poll knows what "changed"
 * means — the hook does not know the prior value on its own.
 */
export function useRecalibrateTakes(templateId: string) {
  const queryClient = useQueryClient();
  const [isRecalibrating, setIsRecalibrating] = useState(false);
  const baselineComputedAt = useRef<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => recalibrateTakes(templateId),
  });

  // Only actually polls (refetchInterval) once isRecalibrating flips true —
  // shares the ['calibration-status', templateId] cache with
  // useCalibrationStatus above without disturbing that hook's own
  // (much longer) staleTime.
  const pollQuery = useQuery({
    queryKey: ['calibration-status', templateId],
    queryFn: () => getCalibrationStatus(templateId),
    enabled: isRecalibrating,
    refetchInterval: isRecalibrating ? POLLING_INTERVAL_MS : false,
  });

  useEffect(() => {
    if (!isRecalibrating) return;
    // Guard on isSuccess, not merely on `data` being present: when `enabled`
    // first flips true, `pollQuery.data` is `undefined` for one or more
    // renders before the first fetch resolves. Comparing that transient
    // `undefined` (-> null) against a real baseline string would look like
    // "changed" and clear isRecalibrating before any actual re-measurement
    // happened. Wait for a genuine fetched result.
    if (!pollQuery.isSuccess) return;
    const freshComputedAt = pollQuery.data?.computed_at ?? null;
    if (freshComputedAt !== baselineComputedAt.current) {
      setIsRecalibrating(false);
      // Refresh the take list + summary the rest of the panel reads so the
      // chip, take rows, and grade-scale status all pick up the new numbers
      // in the same paint the button stops spinning.
      queryClient.invalidateQueries({ queryKey: ['calibration-takes', templateId] });
      queryClient.invalidateQueries({ queryKey: ['calibration-status', templateId] });
    }
  }, [isRecalibrating, pollQuery.isSuccess, pollQuery.data?.computed_at, queryClient, templateId]);

  const trigger = async (currentComputedAt: string | null) => {
    baselineComputedAt.current = currentComputedAt;
    await mutation.mutateAsync();
    setIsRecalibrating(true);
  };

  return {
    trigger,
    /** True from the POST until computed_at is observed to change. */
    isRecalibrating,
    /** True only for the brief in-flight window of the POST itself. */
    isTriggering: mutation.isPending,
    triggerError: mutation.error instanceof Error ? mutation.error.message : null,
  };
}

// ---------------------------------------------------------------------------
// Example Performances (backlog/example-performances-template-resources.md,
// PR 1+2, owner-approved 2026-09-23)
// ---------------------------------------------------------------------------

/**
 * List the athlete-visible "example performances" for a template — the
 * label-only, no-grade/no-score payload rendered by ExamplePerformancesPanel.
 * `enabled` follows `templateId` the same way every other calibration-takes
 * query does; there is no polling here (the list only changes when a coach
 * flips a toggle, which already invalidates `['calibration-takes', ...]` —
 * this is a SEPARATE cache key on a SEPARATE endpoint, so it is invalidated
 * independently by `useUpdateCalibrationTake` below).
 */
export function useVisibleExamplePerformances(templateId: string | undefined) {
  return useQuery({
    queryKey: ['example-performances', templateId],
    queryFn: () => listVisibleExamplePerformances(templateId!),
    enabled: !!templateId,
    staleTime: 5_000,
  });
}
